import type { QueryKey } from '@tanstack/react-query';
import { events } from 'fetch-event-stream';
import _get from 'lodash/get';
import { executeFetch } from './fetch';
import type { FetchOptions } from './fetch/types';

// Function to safely parse JSON strings
const parseJson = (message: string): unknown => {
  try {
    return JSON.parse(message);
  } catch (e) {
    return {};
  }
};

// Function to fetch data using Server-Sent Events (SSE)
const fetchSSE = async <T>(
  queryArgs: { queryKey: QueryKey; signal: AbortSignal; meta: FetchOptions['meta'] },
  onMessage: (resp: T) => void,
): Promise<T> => {
  const url = queryArgs.queryKey[0] as string;
  const body = queryArgs.queryKey[1] as never;

  // Manually fetch a Response using the provided URL and body
  const res = await executeFetch<Response>(
    { url, data: body, method: 'POST', headers: { 'Content-Type': 'application/json' } },
    queryArgs,
  );

  if (res.ok) {
    let resolve!: ((_: T) => void) | null;
    let reject!: ((_: unknown) => void) | null;
    const promise = new Promise<T>((_resolve, rej) => {
      resolve = _resolve;
      reject = rej;
    });

    // Create an event stream from the response
    const stream = events(res, queryArgs.signal);
    for await (const event of stream) {
      // type of event stream supported, message or error
      switch (event.event) {
        // message event sent by the server, for all message except errors
        case 'message':
          try {
            if (event.data) {
              const resp = parseJson(event.data) as T;
              onMessage(resp);
              // break and complete the stream if response is marked as completed
              if (_get(resp, ['completed'])) {
                // resolve function is not called as it modifies the data through useQuery
                if (resolve) {
                  // if we do not call this then the query is completed but the consumer of useSSEQuerySubscription would not know and isFetching stays true all the time
                  resolve(null as T);
                }
                break;
              }
            }
          } catch (e) {
            console.error('Error Parsing Message:', e);
            reject?.(e);
          }
          break;
        case 'error':
        default:
          console.error('Error in SSE:', event.data);
          if (reject) {
            reject(event.data || new Error('Unknown error in SSE'));
            reject = null;
          }
      }
    }
    return promise as T;
  }

  return res as T;
};

export { fetchSSE };
export default fetchSSE;
